import { route } from 'quasar/wrappers'
import { createRouter, createMemoryHistory, createWebHistory, createWebHashHistory } from 'vue-router'
import routes from './routes'
import { useAuthStore } from '@/stores/auth'
import {
  LOCATION_WARNING_TOAST_OPTIONS,
  messageOpenRegisterInLocation
} from '@/utils/registerLocationMessaging'
import { userHasAbility, userHasRole } from '@/utils/permissions'
import { isEventsFeatureActive } from '@/utils/eventsFeatureHelpers'
import { isLeadsFeatureActive } from '@/utils/leadsFeatureHelpers'
import { isInvoicesFeatureActive, isProjectsFeatureActive } from '@/utils/projectFeatureHelpers'

async function resolveAuthenticatedLandingPath (authStore) {
  const abilities = authStore.user?.abilities || []
  if (abilities.includes('reports.view')) {
    return '/dashboard'
  }
  if (abilities.includes('pos.sell') || abilities.includes('pos.open-close-register')) {
    try {
      const { useRegisterStore } = await import('@/stores/register')
      const registerStore = useRegisterStore()
      await registerStore.loadCurrentUserRegister()
      return registerStore.currentRegister?.is_open ? '/sales/register' : '/registers'
    } catch {
      return '/registers'
    }
  }
  if (abilities.includes('customers.view')) {
    return '/customers'
  }
  if (abilities.includes('inventory.view')) {
    return '/products'
  }
  return '/dashboard'
}

async function resolveAbilityDeniedRedirect (user) {
  const abilities = user?.abilities || []
  if (abilities.includes('reports.view') || userHasRole(user, 'owner')) {
    return '/dashboard'
  }
  if (abilities.includes('pos.sell') || abilities.includes('pos.open-close-register')) {
    try {
      const { useRegisterStore } = await import('@/stores/register')
      const registerStore = useRegisterStore()
      await registerStore.loadCurrentUserRegister()
      return registerStore.currentRegister?.is_open ? '/sales/register' : '/registers'
    } catch {
      return '/registers'
    }
  }
  if (abilities.includes('customers.view')) {
    return '/customers'
  }
  if (abilities.includes('inventory.view')) {
    return '/products'
  }
  return '/login'
}

/*
 * If not building with SSR mode, you can
 * directly export the Router instantiation;
 *
 * The function below can be async too; either use
 * async/await or return a Promise which resolves
 * with the Router instance.
 */

export default route(function (/* { store, ssrContext } */) {
  const createHistory = import.meta.env.QUASAR_SERVER
    ? createMemoryHistory
    : (import.meta.env.QUASAR_VUE_ROUTER_MODE === 'history' ? createWebHistory : createWebHashHistory)

  // If we've switched the SPA to history mode, migrate old hash URLs (/#/...) to
  // their equivalent history URLs (/...).
  if (!import.meta.env.QUASAR_SERVER && import.meta.env.QUASAR_VUE_ROUTER_MODE === 'history') {
    const rawHash = window.location.hash || ''
    if (rawHash.startsWith('#/')) {
      const hashPath = rawHash.slice(1) // "/some/route?query=1"
      const target = hashPath.includes('?')
        ? hashPath
        : `${hashPath}${window.location.search || ''}`
      window.location.replace(target)
      return
    }
  }

  const Router = createRouter({
    scrollBehavior: () => ({ left: 0, top: 0 }),
    routes,

    // Leave this as is and make changes in quasar.conf.js instead!
    // quasar.conf.js -> build -> vueRouterMode
    // quasar.conf.js -> build -> publicPath
    history: createHistory(import.meta.env.QUASAR_VUE_ROUTER_BASE)
  })

  Router.beforeEach(async (to) => {
    const authStore = useAuthStore()

    // Only check auth if the route requires it
    if (to.meta.requiresAuth) {
      try {
        await authStore.checkAuth()
      } catch {
        return '/login'
      }
    }

    // Handle authenticated users trying to access login page
    if (to.path === '/login' && authStore.isAuthenticated) {
      if (authStore.needsPasswordReset) {
        return '/set-password'
      }
      return resolveAuthenticatedLandingPath(authStore)
    }

    // Check if user is authenticated but we haven't detected it yet
    // This handles the case where someone opens a new tab to login but has an active session
    if (to.path === '/login' && !authStore.isAuthenticated) {
      if (await authStore.hasActiveSession()) {
        try {
          await authStore.checkAuth()
          if (authStore.needsPasswordReset) {
            return '/set-password'
          }
          return resolveAuthenticatedLandingPath(authStore)
        } catch {
          // User is not authenticated, stay on login page
        }
      }
    }

    // Handle password reset requirement
    if (authStore.isAuthenticated && authStore.needsPasswordReset && to.path !== '/set-password') {
      return { path: '/set-password', replace: true }
    }

    // Check if user is trying to access sales register without an open register
    if (to.path === '/sales/register' && authStore.isAuthenticated) {
      const isGuardSessionActive = () => authStore.isAuthenticated
      try {
        const { useRegisterStore } = await import('@/stores/register')
        const { useLocationStore } = await import('@/stores/location')
        const registerStore = useRegisterStore()
        const locationStore = useLocationStore()

        if (!isGuardSessionActive()) return '/login'

        const scopedCurrentRegister = await registerStore.loadCurrentUserRegister({ force: true })
        if (!isGuardSessionActive()) return '/login'

        await locationStore.fetchLocations()
        if (!isGuardSessionActive()) return '/login'

        await locationStore.initializeCurrentLocation()
        if (!isGuardSessionActive()) return '/login'

        console.log('Router guard - checking register status:', {
          currentRegister: registerStore.currentRegister,
          isOpen: registerStore.currentRegister?.is_open,
          hasRegister: !!registerStore.currentRegister
        })

        if (!scopedCurrentRegister || !scopedCurrentRegister.is_open) {
          const openElsewhere = await registerStore.loadCurrentUserRegister({ force: true, includeAllLocations: true })
          if (openElsewhere?.is_open) {
            const registerLocationName = openElsewhere.location_name || `location #${openElsewhere.location_id}`
            const { useNotificationStore } = await import('@/stores/notification')
            const notificationStore = useNotificationStore()
            notificationStore.delayedWarning(
              messageOpenRegisterInLocation(registerLocationName),
              LOCATION_WARNING_TOAST_OPTIONS
            )
            return { path: '/registers', replace: true }
          }

          console.log('Router guard - redirecting: no open register found')

          const { useNotificationStore } = await import('@/stores/notification')
          const notificationStore = useNotificationStore()
          notificationStore.delayedWarning('Please open a register to access the sales register screen')

          return { path: '/registers', replace: true }
        }

        const selectedLocationId = locationStore.getCurrentLocation?.id
        const registerLocationId = scopedCurrentRegister?.location_id
        if (selectedLocationId && registerLocationId && selectedLocationId !== registerLocationId) {
          console.log('Router guard - redirecting: selected location does not match open register location', {
            selectedLocationId,
            registerLocationId
          })

          const { useNotificationStore } = await import('@/stores/notification')
          const notificationStore = useNotificationStore()
          const registerLocation = locationStore.getLocations?.find?.(loc => loc.id === registerLocationId)
          const registerLocationName = registerLocation?.name || `location #${registerLocationId}`
          notificationStore.delayedWarning(
            messageOpenRegisterInLocation(registerLocationName),
            LOCATION_WARNING_TOAST_OPTIONS
          )

          return { path: '/registers', replace: true }
        }

        console.log('Router guard - allowing access: register is open')
      } catch (error) {
        if (!authStore.isAuthenticated) return '/login'
        console.error('Error checking register status:', error)
        return { path: '/registers', replace: true }
      }
    }

    // Ability-based route enforcement using per-route meta.ability
    if (to.meta?.requiresAuth && authStore.isAuthenticated) {
      const user = authStore.user
      const required = to.meta?.ability

      // Rentals routes require feature toggle (mirrors nav hide)
      if (to.path.startsWith('/rentals')) {
        const { useBusinessStore } = await import('@/stores/business')
        const businessStore = useBusinessStore()
        if (!businessStore.info) {
          await businessStore.fetchInfo()
        }
        if (businessStore.info && !businessStore.info.rentals_enabled) {
          try {
            const { useNotificationStore } = await import('@/stores/notification')
            useNotificationStore().delayedWarning('Rentals is not enabled for this business')
          } catch { /* ignore */ }
          return '/dashboard'
        }
      }

      if (to.meta?.requiresBooking || to.path.startsWith('/booking') || to.path.startsWith('/settings/booking')) {
        const { useBusinessStore } = await import('@/stores/business')
        const businessStore = useBusinessStore()
        if (!businessStore.info) {
          await businessStore.fetchInfo()
        }
        if (businessStore.info && !businessStore.info.booking_enabled) {
          try {
            const { useNotificationStore } = await import('@/stores/notification')
            useNotificationStore().delayedWarning('Booking is not enabled for this business')
          } catch { /* ignore */ }
          return '/dashboard'
        }
      }

      if (to.meta?.requiresScheduling || to.path.startsWith('/scheduling') || to.path.startsWith('/settings/scheduling')) {
        const { useBusinessStore } = await import('@/stores/business')
        const businessStore = useBusinessStore()
        if (!businessStore.info) {
          await businessStore.fetchInfo()
        }
        if (businessStore.info?.booking_enabled) {
          try {
            const { useNotificationStore } = await import('@/stores/notification')
            useNotificationStore().delayedWarning('Booking replaces scheduling for this business')
          } catch { /* ignore */ }
          return '/dashboard'
        }
      }

      if (to.path.startsWith('/settings/staff')) {
        const { useBusinessStore } = await import('@/stores/business')
        const businessStore = useBusinessStore()
        if (!businessStore.info) {
          await businessStore.fetchInfo()
        }
        if (businessStore.info && !businessStore.info.booking_enabled) {
          try {
            const { useNotificationStore } = await import('@/stores/notification')
            useNotificationStore().delayedWarning('Booking is not enabled for this business')
          } catch { /* ignore */ }
          return '/settings'
        }
      }

      if (to.meta?.requiresInvoices || to.path.startsWith('/invoices') || to.path.startsWith('/settings/invoices')) {
        const { useBusinessStore } = await import('@/stores/business')
        const businessStore = useBusinessStore()
        if (!businessStore.info) {
          await businessStore.fetchInfo()
        }
        if (businessStore.info && !isInvoicesFeatureActive(businessStore.info)) {
          try {
            const { useNotificationStore } = await import('@/stores/notification')
            useNotificationStore().delayedWarning('Invoices is not enabled for this business')
          } catch { /* ignore */ }
          return '/dashboard'
        }
      }

      if (to.meta?.requiresProjects || to.path.startsWith('/projects')) {
        const { useBusinessStore } = await import('@/stores/business')
        const businessStore = useBusinessStore()
        if (!businessStore.info) {
          await businessStore.fetchInfo()
        }
        if (businessStore.info && !isProjectsFeatureActive(businessStore.info)) {
          try {
            const { useNotificationStore } = await import('@/stores/notification')
            useNotificationStore().delayedWarning('Projects is not enabled for this business')
          } catch { /* ignore */ }
          return '/dashboard'
        }
      }

      if (to.path.startsWith('/events')) {
        const { useBusinessStore } = await import('@/stores/business')
        const businessStore = useBusinessStore()
        if (!businessStore.info) {
          await businessStore.fetchInfo()
        }
        if (businessStore.info && !isEventsFeatureActive(businessStore.info)) {
          try {
            const { useNotificationStore } = await import('@/stores/notification')
            useNotificationStore().delayedWarning('Events is not enabled for this business')
          } catch { /* ignore */ }
          return '/dashboard'
        }
      }

      if (to.path.startsWith('/leads') || to.path.startsWith('/settings/intake-form')) {
        const { useBusinessStore } = await import('@/stores/business')
        const businessStore = useBusinessStore()
        if (!businessStore.info) {
          await businessStore.fetchInfo()
        }
        if (businessStore.info && !isLeadsFeatureActive(businessStore.info)) {
          try {
            const { useNotificationStore } = await import('@/stores/notification')
            useNotificationStore().delayedWarning('Leads is not enabled for this business')
          } catch { /* ignore */ }
          return to.path.startsWith('/settings/') ? '/settings' : '/dashboard'
        }
      }

      if (required && !userHasAbility(user, required)) {
        console.log('Router guard - insufficient abilities, redirecting:', {
          userAbilities: user?.abilities,
          userRoles: user?.roles,
          requiredAbility: required
        })

        try {
          const { useNotificationStore } = await import('@/stores/notification')
          useNotificationStore().delayedWarning('You do not have access to that page')
        } catch (error) {
          console.error('Error checking register status:', error)
        }

        return resolveAbilityDeniedRedirect(user)
      }
    }
  })

  return Router
})
